import { useState, useEffect, useRef, useCallback } from "react";
import { searchEdges } from "../services/edges/search";
import { useDebounce } from "../../../hooks/useDebounce";
import useUser from "../../../hooks/useUser";

const buildPreviewRecordFromEdge = (item) => {
  if (!item) return null;

  const edge = item.edge ?? {};
  const recordId = edge.recordId ?? item.id;
  if (!recordId) return null;

  const preview = edge.preview ?? {};
  const updatedAt = edge.query?.recordUpdatedAt ?? edge.updatedAt ?? null;

  return {
    id: recordId,
    access: {
      owner: edge.ownerId ?? null
    },
    content: {
      title: typeof preview.title === "string" ? preview.title : "",
      tags: Array.isArray(preview.tags) ? preview.tags : []
    },
    metadata: {
      updatedAt
    },
    _isPreview: true
  };
};

export function useRecordSearch(currentRecordId = null) {
  const { uid } = useUser();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [recentRecords, setRecentRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedRecent, setHasLoadedRecent] = useState(false);

  const debouncedQuery = useDebounce(query, 300);
  const searchRequestIdRef = useRef(0);
  const recentRequestIdRef = useRef(0);
  const latestQueryRef = useRef(query);

  useEffect(() => {
    latestQueryRef.current = query;
  }, [query]);

  useEffect(() => {
    // Invalidate in-flight requests on auth changes and clear stale state.
    searchRequestIdRef.current += 1;
    recentRequestIdRef.current += 1;
    setResults([]);
    setRecentRecords([]);
    setHasLoadedRecent(false);
    setIsLoading(false);
    setError(null);
  }, [uid]);

  // Load recent records on demand
  const loadRecentRecords = useCallback(async () => {
    if (!uid || hasLoadedRecent) return; // Don't reload if already loaded

    const requestId = ++recentRequestIdRef.current;

    try {
      const result = await searchEdges({
        uid,
        viewType: "note",
        scope: { mode: "users", userIds: [uid] },
        searchTerm: "",
        ordering: "recent",
        pageSize: 10,
        cursor: null
      });

      if (recentRequestIdRef.current !== requestId) {
        return;
      }

      const records = (result.items || [])
        .map(buildPreviewRecordFromEdge)
        .filter(Boolean);

      setRecentRecords(records);
      setHasLoadedRecent(true);
      
      // If no query, set recent records as results
      if (!latestQueryRef.current.trim()) {
        const filtered = currentRecordId
          ? records.filter((record) => record.id !== currentRecordId)
          : records;
        setResults(filtered);
      }
    } catch (err) {
      if (recentRequestIdRef.current !== requestId) {
        return;
      }
      console.error("Failed to load recent records:", err);
      setError(err);
    }
  }, [uid, hasLoadedRecent, currentRecordId]);

  // Search records when query changes
  useEffect(() => {
    if (!uid) return;
    if (!debouncedQuery.trim()) {
      searchRequestIdRef.current += 1;
      setIsLoading(false);
      // When query is empty, show recent records if they've been loaded
      if (hasLoadedRecent) {
        const filtered = currentRecordId
          ? recentRecords.filter((record) => record.id !== currentRecordId)
          : recentRecords;
        setResults(filtered);
      } else {
        setResults([]);
      }
      return;
    }

    const searchRecords = async () => {
      const requestId = ++searchRequestIdRef.current;
      setIsLoading(true);
      setError(null);

      try {
        const result = await searchEdges({
          uid,
          viewType: "note",
          scope: { mode: "users", userIds: [uid] },
          searchTerm: debouncedQuery,
          pageSize: 20,
          cursor: null
        });

        if (searchRequestIdRef.current !== requestId) {
          return;
        }

        const mapped = (result.items || [])
          .map(buildPreviewRecordFromEdge)
          .filter(Boolean)
          .filter((record) => record.id !== currentRecordId);

        setResults(mapped);
      } catch (err) {
        if (searchRequestIdRef.current !== requestId) {
          return;
        }
        setError(err);
        setResults([]);
      } finally {
        if (searchRequestIdRef.current === requestId) {
          setIsLoading(false);
        }
      }
    };

    searchRecords();
  }, [debouncedQuery, currentRecordId, recentRecords, hasLoadedRecent, uid]);

  return {
    query,
    setQuery,
    results,
    isLoading,
    error,
    isShowingRecent: !query.trim() && hasLoadedRecent,
    loadRecentRecords,
  };
}
